import React, { useEffect, useState } from 'react';
import AppShell from '../components/AppShell';
import api from '../lib/api';
import { errMsg } from '../lib/errors';
import { LoadingBlock, EmptyState } from '../components/States';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Plus, Pencil, Trash2, Handshake, Search, MapPin, X } from 'lucide-react';
import { toast } from 'sonner';

const emptyForm = {
    name: '', gstin: '', pan: '', contact_name: '', phone: '', email: '',
    address_line1: '', address_line2: '', area: '', city: '', district: '',
    state: '', pincode: '', country: 'India',
    category_ids: [], brand_ids: [], notes: '', active: true,
};

function MultiPicker({ label, options, value, onChange, testid }) {
    // options: [{id,name}]; value: string[]
    const selected = options.filter(o => value.includes(o.id));
    const [open, setOpen] = useState(false);
    return (
        <div>
            <Label>{label}</Label>
            <div className="rounded-md border p-2 min-h-[40px] flex flex-wrap gap-1 cursor-pointer" onClick={() => setOpen(o => !o)} data-testid={testid}>
                {selected.length === 0 && <span className="text-xs text-neutral-400 py-0.5">Click to select…</span>}
                {selected.map(o => (
                    <span key={o.id} className="inline-flex items-center gap-1 rounded bg-green-100 text-green-800 px-2 py-0.5 text-xs">
                        {o.name}
                        <button type="button" onClick={(e) => { e.stopPropagation(); onChange(value.filter(x => x !== o.id)); }}>
                            <X className="h-3 w-3" />
                        </button>
                    </span>
                ))}
            </div>
            {open && (
                <div className="mt-1 rounded-md border max-h-40 overflow-y-auto bg-white shadow">
                    {options.map(o => {
                        const checked = value.includes(o.id);
                        return (
                            <label key={o.id} className={`flex items-center gap-2 px-3 py-1.5 text-sm cursor-pointer hover:bg-neutral-50 ${checked ? 'bg-green-50' : ''}`}>
                                <input type="checkbox" checked={checked} onChange={() => onChange(checked ? value.filter(x => x !== o.id) : [...value, o.id])} data-testid={`${testid}-opt-${o.id}`} />
                                <span>{o.name}</span>
                            </label>
                        );
                    })}
                    {options.length === 0 && <div className="px-3 py-2 text-xs text-neutral-500">No options</div>}
                </div>
            )}
        </div>
    );
}

export default function Suppliers() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState('');
    const [dialog, setDialog] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(emptyForm);
    const [categories, setCategories] = useState([]);
    const [brands, setBrands] = useState([]);
    const [pinLookupOpts, setPinLookupOpts] = useState([]);
    const [pinLoading, setPinLoading] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const r = await api.get('/suppliers', { params: q ? { q } : {} });
            setItems(r.data.items || r.data);
        } finally { setLoading(false); }
    };
    useEffect(() => {
        (async () => {
            const [cr, br] = await Promise.all([api.get('/categories'), api.get('/brands')]);
            // only top-level (root) categories for supplier mapping
            setCategories((cr.data || []).filter(c => !c.parent_id));
            setBrands(br.data || []);
        })();
        load();
    }, []);

    const openNew = () => { setEditing(null); setForm(emptyForm); setPinLookupOpts([]); setDialog(true); };
    const openEdit = (s) => {
        setEditing(s);
        setForm({
            name: s.name || '', gstin: s.gstin || '', pan: s.pan || '',
            contact_name: s.contact_name || '', phone: s.phone || '', email: s.email || '',
            address_line1: s.address_line1 || '', address_line2: s.address_line2 || '',
            area: s.area || '', city: s.city || '', district: s.district || '',
            state: s.state || '', pincode: s.pincode || '', country: s.country || 'India',
            category_ids: s.category_ids || [], brand_ids: s.brand_ids || [],
            notes: s.notes || '', active: s.active !== false,
        });
        setPinLookupOpts([]);
        setDialog(true);
    };

    const lookupPincode = async () => {
        if (!/^\d{6}$/.test(form.pincode)) { toast.error('Enter a valid 6-digit pincode'); return; }
        setPinLoading(true); setPinLookupOpts([]);
        try {
            const r = await api.get(`/shipping/pincode/${form.pincode}`);
            const list = r.data.post_offices || [];
            if (list.length === 0) { toast.error('No records found'); return; }
            setPinLookupOpts(list);
            const p = list[0];
            setForm(f => ({ ...f, area: p.post_office, district: p.district, state: p.state, country: p.country || 'India' }));
            if (list.length > 1) toast.info(`${list.length} locations found — pick the correct one`);
        } catch (e) { toast.error(errMsg(e, 'Lookup failed')); }
        finally { setPinLoading(false); }
    };

    const pickArea = (poName) => {
        const p = pinLookupOpts.find(x => x.post_office === poName);
        if (p) setForm(f => ({ ...f, area: p.post_office, district: p.district, state: p.state }));
    };

    const save = async () => {
        if (!form.name.trim()) { toast.error('Name required'); return; }
        try {
            if (editing) await api.patch(`/suppliers/${editing.id}`, form);
            else await api.post('/suppliers', form);
            toast.success('Saved'); setDialog(false); load();
        } catch (e) { toast.error(errMsg(e, 'Save failed')); }
    };
    const doDelete = async (s) => {
        if (!window.confirm(`Delete supplier "${s.name}"?`)) return;
        try { await api.delete(`/suppliers/${s.id}`); toast.success('Deleted'); load(); }
        catch (e) { toast.error(errMsg(e, 'Delete failed')); }
    };

    return (
        <AppShell pageTitle="Suppliers" pageActions={
            <Button onClick={openNew} data-testid="add-supplier-button" style={{ background: 'hsl(142 71% 45%)', color: 'white' }}>
                <Plus className="h-4 w-4 mr-2" />Add Supplier
            </Button>
        }>
            <div className="flex items-center gap-2 mb-4">
                <div className="relative flex-1 max-w-md">
                    <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
                    <Input value={q} onChange={e => setQ(e.target.value)} onKeyDown={e => e.key === 'Enter' && load()} placeholder="Search name / GSTIN / city…" className="pl-9" data-testid="supplier-search" />
                </div>
                <Button variant="outline" onClick={load}>Search</Button>
            </div>
            {loading ? <LoadingBlock /> : items.length === 0 ? (
                <EmptyState icon={Handshake} title="No suppliers yet" description="Add suppliers to track where your inventory comes from." />
            ) : (
                <Card className="p-0 overflow-x-auto">
                    <table className="w-full text-sm" data-testid="suppliers-table">
                        <thead>
                            <tr className="border-b bg-neutral-50 text-xs text-neutral-500">
                                <th className="text-left p-3 font-medium">Supplier</th>
                                <th className="text-left font-medium">GSTIN</th>
                                <th className="text-left font-medium">Location</th>
                                <th className="text-left font-medium">Categories</th>
                                <th className="text-left font-medium">Status</th>
                                <th className="text-right p-3 font-medium">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map(s => (
                                <tr key={s.id} className="border-b hover:bg-neutral-50" data-testid={`supplier-row-${s.id}`}>
                                    <td className="p-3">
                                        <div className="font-medium">{s.name}</div>
                                        <div className="text-xs text-neutral-500">{s.contact_name || s.phone || s.email || '—'}</div>
                                    </td>
                                    <td className="font-mono text-xs">{s.gstin || '—'}</td>
                                    <td className="text-xs">{[s.city, s.state, s.pincode].filter(Boolean).join(', ') || '—'}</td>
                                    <td className="text-xs">{(s.category_ids || []).length} mapped</td>
                                    <td>{s.active ? <span className="text-green-700 text-xs">Active</span> : <span className="text-neutral-500 text-xs">Inactive</span>}</td>
                                    <td className="p-3 text-right">
                                        <Button variant="ghost" size="icon" onClick={() => openEdit(s)}><Pencil className="h-4 w-4" /></Button>
                                        <Button variant="ghost" size="icon" onClick={() => doDelete(s)}><Trash2 className="h-4 w-4 text-red-600" /></Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </Card>
            )}

            <Dialog open={dialog} onOpenChange={setDialog}>
                <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
                    <DialogHeader><DialogTitle>{editing ? 'Edit Supplier' : 'New Supplier'}</DialogTitle></DialogHeader>
                    <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-3">
                            <div><Label>Supplier Name *</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="supplier-name" /></div>
                            <div><Label>Contact Person</Label><Input value={form.contact_name} onChange={e => setForm({ ...form, contact_name: e.target.value })} /></div>
                            <div><Label>Phone</Label><Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} data-testid="supplier-phone" /></div>
                            <div><Label>Email</Label><Input value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} /></div>
                            <div><Label>GSTIN</Label><Input value={form.gstin} onChange={e => setForm({ ...form, gstin: e.target.value.toUpperCase() })} placeholder="e.g. 27ABCDE1234F1Z5" data-testid="supplier-gstin" /></div>
                            <div><Label>PAN</Label><Input value={form.pan} onChange={e => setForm({ ...form, pan: e.target.value.toUpperCase() })} /></div>
                        </div>

                        {/* Pincode Lookup */}
                        <div className="rounded-lg border p-3 bg-neutral-50/60">
                            <div className="text-sm font-semibold mb-2 flex items-center gap-2"><MapPin className="h-4 w-4" />Address (Pincode Auto-Lookup)</div>
                            <div className="grid grid-cols-3 gap-3 items-end">
                                <div>
                                    <Label>Pincode *</Label>
                                    <div className="flex gap-1">
                                        <Input value={form.pincode} onChange={e => setForm({ ...form, pincode: e.target.value.replace(/\D/g, '').slice(0, 6) })} maxLength={6} data-testid="supplier-pincode" />
                                        <Button type="button" variant="outline" size="sm" onClick={lookupPincode} disabled={pinLoading || form.pincode.length !== 6} data-testid="supplier-pin-lookup">
                                            <Search className="h-3.5 w-3.5" />
                                        </Button>
                                    </div>
                                </div>
                                {pinLookupOpts.length > 1 ? (
                                    <div className="col-span-2">
                                        <Label>Area / Post Office</Label>
                                        <select value={form.area} onChange={e => pickArea(e.target.value)} className="flex h-9 w-full rounded-md border border-input px-3 py-1 text-sm" data-testid="supplier-po-select">
                                            {pinLookupOpts.map(p => <option key={p.post_office} value={p.post_office}>{p.post_office} — {p.district}, {p.state}</option>)}
                                        </select>
                                    </div>
                                ) : (
                                    <div className="col-span-2"><Label>Area</Label><Input value={form.area} onChange={e => setForm({ ...form, area: e.target.value })} /></div>
                                )}
                            </div>
                            <div className="grid grid-cols-3 gap-3 mt-3">
                                <div><Label>City</Label><Input value={form.city} onChange={e => setForm({ ...form, city: e.target.value })} /></div>
                                <div><Label>District</Label><Input value={form.district} onChange={e => setForm({ ...form, district: e.target.value })} /></div>
                                <div><Label>State</Label><Input value={form.state} onChange={e => setForm({ ...form, state: e.target.value })} data-testid="supplier-state" /></div>
                            </div>
                            <div className="grid grid-cols-3 gap-3 mt-3">
                                <div className="col-span-2"><Label>Address Line 1</Label><Input value={form.address_line1} onChange={e => setForm({ ...form, address_line1: e.target.value })} /></div>
                                <div><Label>Country</Label><Input value={form.country} onChange={e => setForm({ ...form, country: e.target.value })} /></div>
                            </div>
                            <div className="mt-3"><Label>Address Line 2</Label><Input value={form.address_line2} onChange={e => setForm({ ...form, address_line2: e.target.value })} /></div>
                        </div>

                        <MultiPicker label="Main Categories Supplied *" options={categories.map(c => ({ id: c.id, name: c.name }))}
                            value={form.category_ids} onChange={v => setForm({ ...form, category_ids: v })} testid="supplier-cat-picker" />

                        <MultiPicker label="Brands Supplied (optional)" options={brands.map(b => ({ id: b.id, name: b.name }))}
                            value={form.brand_ids} onChange={v => setForm({ ...form, brand_ids: v })} testid="supplier-brand-picker" />

                        <div><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Payment terms, delivery lead time, etc." /></div>
                        <div className="flex items-center gap-2"><Switch checked={form.active} onCheckedChange={v => setForm({ ...form, active: v })} /><Label>Active</Label></div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDialog(false)}>Cancel</Button>
                        <Button onClick={save} data-testid="save-supplier-button" style={{ background: 'hsl(142 71% 45%)', color: 'white' }}>Save</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </AppShell>
    );
}
